import React from 'react';

const Step2NewPatient = React.memo(function Step2NewPatient({
  newPatientData,
  handleNewPatientChange,
  handleNewPatientSubmit,
  loading,
  debugMode,
  zipcodeData,
  insuranceNumber,
  setInsuranceNumber,
  preferredLanguage,
  setPreferredLanguage,
  visitTypes,
  selectedVisitType,
  handleVisitTypeChange,
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">New Patient Information</h2>
        <p className="text-gray-600">Please provide your information to create your patient record</p>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
        <div className="flex items-start">
          <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> If you already have an account with us, please use the "Existing Patient" form to ensure your appointment is properly scheduled. For questions, call <span className="font-semibold">(530) 806-0805</span>.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleNewPatientSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
            <input
              type="text"
              name="first_name"
              value={newPatientData.first_name}
              onChange={handleNewPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
            <input
              type="text"
              name="last_name"
              value={newPatientData.last_name}
              onChange={handleNewPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
            <input
              type="date"
              name="dob"
              value={(() => {
                // State stores MM/DD/YYYY; date input needs YYYY-MM-DD
                if (newPatientData.dob && /^\d{2}\/\d{2}\/\d{4}$/.test(newPatientData.dob)) {
                  const [m, d, y] = newPatientData.dob.split('/');
                  return `${y}-${m}-${d}`;
                }
                return newPatientData.dob || '';
              })()}
              onChange={handleNewPatientChange}
              required
              max={new Date().toISOString().split('T')[0]}
              min="1900-01-01"
              className={`w-full px-3 py-2 border rounded-md focus:ring-orange-500 focus:border-orange-500 ${
                newPatientData.dob && /^\d{2}\/\d{2}\/\d{4}$/.test(newPatientData.dob)
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
              }`}
            />
            {newPatientData.dob && /^\d{2}\/\d{2}\/\d{4}$/.test(newPatientData.dob) && (
              <p className="mt-1 text-xs text-green-600 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Date selected: {newPatientData.dob}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sex *</label>
            <select
              name="sex"
              value={newPatientData.sex}
              onChange={handleNewPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Select Sex</option>
              <option value="2">Male</option>
              <option value="1">Female</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={newPatientData.email}
              onChange={handleNewPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Phone *</label>
            <input
              type="tel"
              name="mobile_phone"
              value={newPatientData.mobile_phone}
              onChange={handleNewPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
            <input
              type="text"
              name="address"
              value={newPatientData.address}
              onChange={handleNewPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              placeholder="Street address, City, State"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code *</label>
            <input
              type="text"
              name="zipcode"
              value={newPatientData.zipcode}
              onChange={handleNewPatientChange}
              required
              maxLength="5"
              pattern="[0-9]{5}"
              className={`w-full px-3 py-2 border rounded-md focus:ring-orange-500 focus:border-orange-500 ${
                newPatientData.zipcode && newPatientData.zipcode.length === 5
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
              }`}
              placeholder="12345"
            />
            {debugMode && newPatientData.zipcode && newPatientData.zipcode.length === 5 && zipcodeData && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-xs text-green-800">
                  <strong>✓ {zipcodeData.DisplayName}</strong><br />
                  {zipcodeData.City} (ZIP ID: {zipcodeData.ZipID})
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Partnership of California - Insurance Number *
          </label>
          <input
            type="text"
            name="insurance_number"
            value={insuranceNumber}
            onChange={(e) => setInsuranceNumber(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="Enter your Partnership insurance number (e.g., 123asdf)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Required: Enter your Partnership of California insurance number
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Language *
          </label>
          <input
            type="text"
            name="preferred_language"
            value={preferredLanguage}
            onChange={(e) => setPreferredLanguage(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="Enter preferred language (e.g., English, Spanish, Vietnamese, etc.)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Required: Enter the patient's preferred language for communication
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Comments</label>
          <textarea
            name="comments"
            value={newPatientData.comments}
            onChange={handleNewPatientChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="Enter any additional information (e.g., patient has severe PTSD, special needs, etc.)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional: Include any relevant medical information or special notes
          </p>
        </div>

        {debugMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Visit Type *</label>
            <select
              value={selectedVisitType}
              onChange={handleVisitTypeChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Select a visit type...</option>
              {visitTypes.map((visitType) => (
                <option key={visitType.id} value={visitType.id}>
                  {visitType.name} ({visitType.duration} min) {visitType.isDefault ? '(Default)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              This determines the type of appointment and may affect available time slots
            </p>
          </div>
        )}

        {debugMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg">
            <details className="group">
              <summary className="cursor-pointer p-4 hover:bg-blue-100 transition-colors">
                <h4 className="font-semibold text-blue-800 inline-flex items-center">
                  🔍 Debug: New Patient Data
                  <svg className="w-4 h-4 ml-2 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </h4>
              </summary>
              <div className="px-4 pb-4">
                <pre className="text-xs text-blue-700 overflow-x-auto mb-2">
                  {JSON.stringify(newPatientData, null, 2)}
                </pre>
                <div className="text-xs text-green-600 mt-1">
                  ✓ Date manually entered in MM/DD/YYYY format for API compatibility
                </div>
                <div className="text-xs text-orange-600 mt-1">
                  ⚠️ Address field will return null in response due to upstream API limitation (not a bug)
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  🔄 Using NEW endpoint: /patient/save-new-patient (moved from /booking)
                </div>
                <div className="text-xs text-red-600 mt-1">
                  🚨 ZIP ID is CRITICAL: {newPatientData.zip_id ? `✅ Set (${newPatientData.zip_id})` : '❌ Missing - will cause upstream issues!'}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  🩺 Visit Type: {selectedVisitType ? `${visitTypes.find(vt => vt.id.toString() === selectedVisitType)?.name} (ID: ${selectedVisitType})` : '❌ Not selected'}
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  💳 Insurance Number: {insuranceNumber ? `✅ "${insuranceNumber}"` : '❌ Not entered'}
                </div>
                <div className="text-xs text-indigo-600 mt-1">
                  🌐 Preferred Language: {preferredLanguage ? `✅ "${preferredLanguage}"` : '❌ Not entered'}
                </div>
                <div className="text-xs text-green-600 mt-1">
                  📝 Combined Comments Preview: {(() => {
                    let preview = '';
                    if (insuranceNumber && insuranceNumber.trim() !== '') {
                      const clean = insuranceNumber.replace(/^insurance\s*#?\s*:?\s*/i, '').trim();
                      preview = `Partnership: ${clean}`;
                    }
                    if (preferredLanguage && preferredLanguage.trim() !== '') {
                      if (preview) preview += '\n\nPreferred Language: ' + preferredLanguage.trim();
                      else preview = 'Preferred Language: ' + preferredLanguage.trim();
                    }
                    if (newPatientData.comments && newPatientData.comments.trim() !== '') {
                      if (preview) preview += '\n\n' + newPatientData.comments.trim();
                      else preview = newPatientData.comments.trim();
                    }
                    return preview ? `"${preview}"` : '(empty)';
                  })()}
                </div>
              </div>
            </details>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving Patient Info...
              </>
            ) : (
              <>
                Save & Search Providers
                <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
});

export default Step2NewPatient;
